import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'

/**
 * Reads one Firebase Remote Config parameter as a string.
 *
 * Remote Config is initialised lazily inside an effect so SSR and test
 * environments without a Firebase app do not crash on module import. The SDK
 * persists activated values in IndexedDB, so a returning visitor reads the
 * cached value immediately and only a first-ever visit waits on the network.
 *
 * `ready` is false while the fetch is in flight and true once a result exists,
 * including a null one: any error, or an empty string, resolves to `null`. The
 * caller decides what an absent value means for its feature.
 */
export function useRemoteConfigString(key: string): { value: string | null; ready: boolean } {
  const [remoteConfigReady, setRemoteConfigReady] = useState(false)
  useEffect(() => {
    try {
      const remoteConfig = getRemoteConfig()
      remoteConfig.settings = {
        ...remoteConfig.settings,
        // 1 hour: enough to pick up catalog edits within a session without
        // hammering the Remote Config CDN on every page load.
        minimumFetchIntervalMillis: 60 * 60 * 1000,
        fetchTimeoutMillis: 60 * 1000,
      }
      // A rejection here (offline, a Remote Config outage) would otherwise be
      // an unhandled promise rejection: nothing else in this effect awaits or
      // catches it. Logged rather than swallowed silently so a fetch failure
      // is visible in the console, and `ready` still flips so the caller gets
      // its documented null-value fallback instead of hanging forever.
      void fetchAndActivate(remoteConfig)
        .catch((err) => console.warn(`remote config: fetch for "${key}" failed`, err))
        .finally(() => setRemoteConfigReady(true))
    } catch {
      setRemoteConfigReady(true)
    }
  }, [key])

  const { data, isLoading } = useQuery({
    queryKey: ['remote-config', key] as const,
    enabled: remoteConfigReady,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      try {
        const raw = getValue(getRemoteConfig(), key).asString()
        return raw ? raw : null
      } catch (err) {
        console.warn(`remote config: could not read "${key}"`, err)
        return null
      }
    },
  })

  return { value: data ?? null, ready: remoteConfigReady && !isLoading }
}
